/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { datadogRum } from '@datadog/browser-rum';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { flushSync } from 'react-dom';

import { IMPERSONATE_LAST_PAGE, STAFF_EMAIL, STAFF_ID, STAFF_TOKEN_WHILE_IMPERSONATE } from './constants';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import collectiveApi from 'modules/common/collectiveApi';
import browserHistory from 'modules/common/browserHistory';
import { setAnalyticsUserId } from 'modules/common/components/AnalyticsTracking';
import config from 'modules/common/config';
import { FUNNEL_WORKFLOW_DESTINATIONS, type FunnelWorkflowDestination } from 'modules/common/utils/destinationMapping';
import Token from 'modules/Auth/common/Token.interface';
import User from 'modules/Auth/common/User.interface';
import { Actions, AuthDispatch, AuthHandlers, LoginError } from 'modules/Auth/AuthProvider/Auth.interface';
import { useAuthContext } from 'modules/Auth/AuthProvider';
import { localViews } from 'modules/Auth/AuthProvider/views';
import {
    isPublicRoute,
    setLocalStorage,
    getDecodedToken,
    isTokenExpired,
    lastActiveRoute,
    isFunnelRoute,
    getEmployeeRedirectPath,
    signedOutDestination,
} from 'modules/Auth/AuthProvider/utils';
import { cleanupPersistedMemberData } from 'modules/common/utils/sanitizeMemberData';
import { AUTH0_LOGOUT_PATH } from 'modules/Auth/auth0/config';
import { hasAuth0Session, mayHaveAuth0AppSession, mayHaveAuth0Session } from 'modules/Auth/auth0/session';
import { rememberAfterSignOut, type AfterSignOut } from 'modules/Auth/auth0/afterSignOut';
import { Auth0StaffSessionEndedError, isAuth0StaffImpersonation } from 'modules/Auth/auth0/staffImpersonation';

dayjs.extend(duration);

const log = (message: string) => {
    if (config.debug) {
        // eslint-disable-next-line no-console
        console.log(message);
    }
};

let refreshToken: (
    actions: Actions,
    dispatch: AuthDispatch,
    queryClient?: QueryClient,
    callback?: (...args: unknown[]) => Promise<unknown>
) => Promise<boolean>;

/**
 * Fetch the logged user info and stores it in the AuthContext. With `requireUser`, a body that is
 * not a user -- the error body a 401 resolves with -- is returned unstored: stored, it would pass
 * for a signed-in user.
 */
export async function fetchAuthenticationData(
    actions: Actions,
    dispatch: AuthDispatch,
    options?: { requireUser?: boolean }
) {
    const { data: user } = await collectiveApi.get<User>('rest-auth/user/');
    if (options?.requireUser && !user?.pk) {
        return user;
    }
    flushSync(() => dispatch(actions.setUserData(user)));
    setAnalyticsUserId(user.pk);
    // Identify the RUM session here rather than off the back of a member fetch, so
    // attribution doesn't depend on which parts of the app a session happens to load.
    // Merge rather than assign: `setUser` replaces the whole user object, and the
    // dispatch above has already run `setDebugUsers`, which is what tags an
    // impersonated session with `impersonating` and `staff`.
    datadogRum.setUser({
        ...datadogRum.getUser(),
        id: `${user.pk}`,
        name: [user.first_name, user.last_name].filter(Boolean).join(' '),
        email: user.email,
    });

    return user;
}

/**
 * Ends the impersonation at the backend, sending the member's refresh token for it to revoke.
 * Read when the request is sent, not built: the request interceptor may have just rotated it.
 */
function deleteImpersonation() {
    const defaults = collectiveApi.defaults.transformRequest ?? [];

    return collectiveApi.delete('impersonate/', {
        transformRequest: [
            () => {
                const refresh = localViews.token()?.refresh;

                return refresh ? { refresh } : undefined;
            },
            ...(Array.isArray(defaults) ? defaults : [defaults]),
        ],
    });
}

/**
 * Ends an impersonation started from an Auth0 staff session. The staffer's session is the
 * SDK's, so only the member's Django session is cleared, and a full page load restores the
 * staffer's. `leave` false stays put, for a sign-out that ends everything after.
 */
async function endAuth0StaffImpersonation(actions: Actions, dispatch: AuthDispatch, { leave }: { leave: boolean }) {
    try {
        await deleteImpersonation();
    } catch (error: any) {
        sendDatadogRumError('impersonateEnd', error, {
            type: 'IMPERSONATE_END',
            staffId: localViews.staffDetails().id,
        });
        // The forced logout that ended the staffer's session is already navigating away.
        if (error instanceof Auth0StaffSessionEndedError) {
            return false;
        }
    } finally {
        datadogRum.stopSession();
        datadogRum.clearUser();
    }

    cleanupPersistedMemberData();

    const lastPage = localStorage.getItem(IMPERSONATE_LAST_PAGE);
    flushSync(() => {
        setLocalStorage(STAFF_EMAIL, null);
        setLocalStorage(STAFF_ID, null);
        setLocalStorage(STAFF_TOKEN_WHILE_IMPERSONATE, null);
        setLocalStorage(IMPERSONATE_LAST_PAGE, null);
        dispatch(actions.clearDjangoSession());
    });

    if (leave) {
        globalThis.location.href = JSON.parse(lastPage || '"/hub-v2"');
    }

    return true;
}

/**
 * Stops the impersonation of a member
 * and restores the staff user session
 */
async function impersonateEnd(actions: Actions, dispatch: AuthDispatch) {
    if (isAuth0StaffImpersonation()) {
        return endAuth0StaffImpersonation(actions, dispatch, { leave: true });
    }

    const staffDetails = localViews.staffDetails();

    const staffId = staffDetails.id;
    const staffToken = staffDetails.token as Token;

    const decodedToken = staffToken?.access && getDecodedToken(staffToken.access);

    if (decodedToken && !isTokenExpired(decodedToken)) {
        try {
            await deleteImpersonation();
        } catch (error: any) {
            sendDatadogRumError('impersonateEnd', error, {
                type: 'IMPERSONATE_END',
                staffId,
            });
        } finally {
            datadogRum.stopSession();
            datadogRum.clearUser();
        }
    } else {
        log('staff token expired');
        datadogRum.stopSession();
        datadogRum.clearUser();
        window.location.href = '/login';

        return false;
    }

    if (!staffToken?.access) {
        log('No staff token found');
        window.location.href = '/login';

        return false;
    }

    cleanupPersistedMemberData();

    flushSync(() => {
        setLocalStorage(STAFF_EMAIL, null);
        setLocalStorage(STAFF_ID, null);
        setLocalStorage(STAFF_TOKEN_WHILE_IMPERSONATE, null);

        dispatch(actions.setToken(staffToken));
    });

    await refreshToken(actions, dispatch);
    await fetchAuthenticationData(actions, dispatch);
    let redirectPath: string | null = '';
    flushSync(() => {
        redirectPath = localStorage.getItem(IMPERSONATE_LAST_PAGE);
        setLocalStorage(IMPERSONATE_LAST_PAGE, null);
    });
    window.location.href = JSON.parse(redirectPath || '"/hub-v2"');

    return true;
}

/**
 * Logs out the user, clears the session data from the AuthProvider
 * and also from the localStorage.
 * If the user is impersonating, it will also stop the impersonation
 */
async function logout(
    actions: Actions,
    dispatch: AuthDispatch,
    queryClient?: QueryClient,
    callback?: (...args: unknown[]) => Promise<unknown>,
    options?: { afterSignOut?: AfterSignOut; keepTenantSession?: boolean }
) {
    // keepTenantSession: a tenant session alone is no reason to sign out through Auth0. Its
    // marker stays, so the next sign-out, or ToDjango, still ends it.
    const endsAuth0 = options?.keepTenantSession ? mayHaveAuth0AppSession() : mayHaveAuth0Session();

    // Under Auth0 the sign-out has to run from /logout, and nothing may be cleared
    // before we get there. Clearing the session while a member route is mounted
    // makes Layout redirect to /login, and that authorize races the Auth0 logout
    // navigation — when it wins, the tenant session is still live and signs the
    // user straight back in. /logout is public, so Layout is not mounted on it.
    if (endsAuth0 && browserHistory.location.pathname !== AUTH0_LOGOUT_PATH) {
        // The caller's callback never runs for an Auth0 sign-out, which ends on the tenant's
        // return to /login, so a different destination has to travel with it.
        if (options?.afterSignOut) {
            rememberAfterSignOut(options.afterSignOut);
        }
        browserHistory.push(AUTH0_LOGOUT_PATH);
        return;
    }

    const isImpersonating = localViews.isImpersonating();
    const user = localViews.user();
    if (isImpersonating && isAuth0StaffImpersonation()) {
        await endAuth0StaffImpersonation(actions, dispatch, { leave: false });
    } else if (isImpersonating) {
        await impersonateEnd(actions, dispatch);
    }
    const refresh = localViews.token()?.refresh;
    if (refresh) {
        try {
            await collectiveApi.post('rest-auth/logout/', {
                refresh,
            });
        } catch (error: any) {
            sendDatadogRumError('Logout failed', error, {
                type: 'ERROR_LOGOUT',
                user,
            });
        }
        datadogRum.stopSession();
    } else if (endsAuth0) {
        // An Auth0 session has no Django refresh token, and its recording must not carry on
        // into the next member's sign-in on this browser.
        datadogRum.stopSession();
    }

    dispatch(actions.clearSession());
    datadogRum.clearUser();
    queryClient?.clear();

    if (callback) {
        await callback();
    }
}

/**
 * Checks local storage for an existing, correct token
 * @param refreshRightAway indicates that the existing token needs to be refreshed
 */
async function checkToken(
    actions: Actions,
    dispatch: AuthDispatch,
    refreshRightAway = false,
    forceUserRefresh = false,
    queryClient?: QueryClient
) {
    const token = localViews.token();

    // An Auth0 session has no Django token by design. Everything below reads the
    // absence of one as an expired session and logs the user out, which for an Auth0
    // session would fire on every page load. The SDK owns its token lifetime, and
    // Auth0SessionGate owns loading the user. A Django token, if one exists, is
    // checked as usual.
    if (!token?.access && hasAuth0Session()) {
        return null;
    }

    // TODO: This shouldn't be in charge of detecting if is public or not
    // we should update the router to be more smart on detecting
    // what's public or not
    if (!token?.access && isPublicRoute(browserHistory.location.pathname)) {
        return null;
    }

    if (!token?.access) {
        const { pathname } = browserHistory.location;
        // Before the logout, which clears the stored email it reads.
        const destination = signedOutDestination(pathname);

        // Before the logout rather than in its callback, which an Auth0 sign-out does not run.
        lastActiveRoute.set();
        await logout(
            actions,
            dispatch,
            queryClient,
            async () => {
                browserHistory.push(destination);
            },
            // A funnel lead goes straight to its magic link: the tenant's logout returns only to a
            // bare /login. With no token and no auth0Session marker there is no app session to
            // end, and the magic link's return to /login ends any tenant session through ToDjango.
            { keepTenantSession: isFunnelRoute(pathname) }
        );
        return null;
    }

    const accessTokenDecoded = getDecodedToken(token.access);
    const diff = dayjs.unix(accessTokenDecoded.exp).diff(dayjs());
    const remainingMillisecs = dayjs.duration(diff).asMilliseconds();

    if (refreshRightAway || remainingMillisecs <= 0) {
        const isTokenRefreshed = await refreshToken(actions, dispatch, queryClient);
        if (isTokenRefreshed && forceUserRefresh) {
            await fetchAuthenticationData(actions, dispatch);
        }
        return isTokenRefreshed;
    }

    // Token is still valid — the axios request interceptor handles proactive
    // refresh before every API call, so no setTimeout scheduling is needed.
    if (forceUserRefresh) {
        await fetchAuthenticationData(actions, dispatch);
    }
    return null;
}

/**
 * Method in charge of refreshing the auth token and
 * checking if the user is still logged in
 */
refreshToken = async (
    actions: Actions,
    dispatch: AuthDispatch,
    queryClient?: QueryClient,
    callback?: (...args: unknown[]) => Promise<unknown>
) => {
    const refresh = localViews.token()?.refresh;
    const user = localViews.user();

    if (!refresh) {
        log('No refresh token found');
        return false;
    }

    if (isTokenExpired(getDecodedToken(refresh))) {
        await logout(actions, dispatch, queryClient);
        return false;
    }

    try {
        const { data } = await collectiveApi.post<Token>('rest-auth/refresh-token/', { refresh });
        if (data?.access) {
            flushSync(() => dispatch(actions.setToken(data)));
            if (callback) {
                await callback(data);
            }
        }
    } catch (err: any) {
        sendDatadogRumError('Token refresh failed', err, {
            type: 'ERROR_TOKEN_REFRESH',
            user,
        });

        // if the refresh token is expired, we logout the user
        if (err.response?.status === 401) {
            await logout(actions, dispatch, queryClient);
            return false;
        }
    }

    return true;
};

/**
 * After a successfull login, there are some methods that are ran to get more data
 * setup the time interval process to refresh the token, but we also add
 * a callback method to be executed after checking the token for any other methods
 * that need to be executed after login
 */
export async function postLoginDataFetch(actions: Actions, dispatch: AuthDispatch) {
    let redirectPath = '/';

    const userData = await fetchAuthenticationData(actions, dispatch);

    await checkToken(actions, dispatch);
    setLocalStorage('loggedin', true);

    const preLogoutRoute = lastActiveRoute.get();
    lastActiveRoute.clear();

    if (preLogoutRoute?.route) {
        window.location.replace(preLogoutRoute.route);
        return redirectPath;
    }

    if (
        userData?.destination &&
        FUNNEL_WORKFLOW_DESTINATIONS.includes(userData.destination as FunnelWorkflowDestination)
    ) {
        // For funnel users, redirect to a neutral page and let Layout handle the final redirect
        redirectPath = '/';
    } else if (localViews.isEmployee()) {
        redirectPath = getEmployeeRedirectPath(localViews.user());
    }

    return redirectPath;
}

/**
 * Logs the user in. Stores the user data in the AuthProvider
 * and also in the localStorage.
 */
async function login(
    actions: Actions,
    dispatch: AuthDispatch,
    email: string,
    password: string,
    fingerprint: string,
    onLoginFailed?: (...args: unknown[]) => Promise<unknown>
) {
    if (localViews.token()?.access) {
        await logout(actions, dispatch);
    }

    const loginErrorResponse: LoginError = {
        success: false,
        errors: {
            message: 'Incorrect email or password. Please check your credentials and try again.',
            requestId: '',
        },
    };
    const username = email.trim();

    let redirectPath = '';
    let redirectPathProps = {};
    let successfulLogin = false;

    try {
        const { data, headers } = await collectiveApi.post<Token>(
            'rest-auth/login/',
            {
                username,
                password,
                fingerprint,
            },
            {
                withCredentials: true,
            }
        );

        if (data) {
            const { access, refresh, ephemeral_token: ephemeralToken, funnel_workflow_id: funnelWorkflowId } = data;
            if (access && refresh) {
                successfulLogin = true;
                // We need to use flushSync to opt-out of automatic batching as this
                // creates a race condition with the axios interceptor that checks
                // for the token in the local storage, resulting in a 401 when the request
                // to /rest-auth/user/ is made.
                // https://react.dev/reference/react-dom/flushSync
                flushSync(() => dispatch(actions.setUserData({ email })));
                flushSync(() => dispatch(actions.setToken(data)));

                redirectPath = await postLoginDataFetch(actions, dispatch);

                if (funnelWorkflowId) {
                    localStorage.setItem('funnel_workflow_id', funnelWorkflowId);
                }
            } else if (ephemeralToken) {
                successfulLogin = true;
                redirectPath = '/verification-code';
                redirectPathProps = {
                    ephemeralToken,
                    fingerprint,
                    email,
                };
            }
        }

        if (!successfulLogin) {
            if (onLoginFailed) {
                await onLoginFailed({ actions, dispatch });
            }
            await logout(actions, dispatch);
            loginErrorResponse.errors.requestId = headers['x-request-id'];
            return loginErrorResponse;
        }

        return {
            success: true,
            data: {
                redirectPath,
                redirectPathProps,
            },
        };
    } catch (error: any) {
        // Do not attach the raw axios error or the email here: the error's
        // `config.data` carries the original request body (email + plaintext
        // password) and would be written to Datadog RUM on every failed login.
        // Only report non-sensitive fields useful for debugging. (INFRA-1521)
        datadogRum.addAction('Login process failed', {
            type: 'ERROR_LOGIN',
            fingerprint,
            status: error?.response?.status,
            requestId: error?.response?.headers?.['x-request-id'],
        });

        if (onLoginFailed) {
            await onLoginFailed({ actions, dispatch });
        }
        logout(actions, dispatch);

        if (error?.response) {
            const { data, headers } = error.response;
            loginErrorResponse.errors = { ...data, requestId: headers['x-request-id'] };
        }

        return loginErrorResponse;
    }
}

/**
 * Verifies the otp code with the ephemeral token in the local storage after
 * a successful login. If the code is correct, it will store the user data
 * and the token in the AuthProvider and also in the localStorage.
 * Also if a callback is passed, it will execute it verification process while
 * retrieving the user info.
 */
async function verifyVerificationCode(
    actions: Actions,
    dispatch: AuthDispatch,
    otp: string,
    email: string,
    fingerprint = null
) {
    try {
        const ephemeralToken = localViews.token()?.ephemeral_token;
        if (!ephemeralToken) {
            return {
                error: 'No ephemeral token found',
                invalidCode: false,
            };
        }

        const response = await collectiveApi.post(`rest-auth/login/code/`, {
            ephemeral_token: ephemeralToken,
            username: email.trim(),
            code: otp,
            fingerprint,
        });

        // axios interceptor for this endpoint returns null if the code is invalid
        // so we need to handle it here
        if (!response || response?.data?.hasErrors) {
            return {
                error: 'Code Invalid.',
                invalidCode: true,
            };
        }

        const { data } = response;
        flushSync(() => dispatch(actions.setToken(data)));
        const redirectPath = await postLoginDataFetch(actions, dispatch);
        return {
            redirectPath,
        };
    } catch (error: unknown | any) {
        sendDatadogRumError('verifyVerificationCode', error, {
            type: 'ERROR_MFA_VERIFICATION',
            fingerprint,
            unhandled: !error.response?.data?.error,
        });

        const defaultErrorMessage =
            'Unable to verify one-time password. Please check your internet connection or try again later.';

        return {
            error: error.response?.data?.error || defaultErrorMessage,
            invalidCode: false,
        };
    }
}

async function sendVerificationCode() {
    try {
        const data = await collectiveApi.post(`rest-auth/login/resend-code/`, {});
        return data;
    } catch (error: unknown | any) {
        sendDatadogRumError('sendVerificationCode', error, {
            type: 'ERROR_MFA_RESEND',
        });

        const defaultErrorMessage = 'Unable to resend otp. Try again later.';
        return {
            error: error.response?.data?.error || defaultErrorMessage,
        };
    }
}

/**
 * Wraps the authentication methods and impersonator feature to use
 * the AuthContext actions and dispatch methods to update it's state
 * @example const { login, logout } = useAuthHandlers();
 */
export default function useAuthHandlers(): AuthHandlers {
    const context = useAuthContext();
    const queryClient = useQueryClient();

    if (!context) {
        throw new Error('useAuthHandlers must be used within an AuthContextProvider');
    }
    const { actions, dispatch } = context;

    return {
        login: (email, password, fingerprint, onLoginFailed) =>
            login(actions, dispatch, email, password, fingerprint, onLoginFailed),
        logout: (callback, options) =>
            logout(
                actions,
                dispatch,
                queryClient,
                async () => {
                    if (callback) {
                        await callback();
                    }
                },
                options
            ),
        impersonateEnd: () => impersonateEnd(actions, dispatch),
        verifyVerificationCode: (otp, fingerprint, email) =>
            verifyVerificationCode(actions, dispatch, otp, email, fingerprint),
        sendVerificationCode: () => sendVerificationCode(),
        checkToken: useCallback(
            (refreshRightAway = false, forceUserRefresh = false) =>
                checkToken(actions, dispatch, refreshRightAway, forceUserRefresh, queryClient),
            [actions, dispatch, queryClient]
        ),
        fetchAuthenticationData: () => fetchAuthenticationData(actions, dispatch),
        actions,
        dispatch,
    };
}
